export const PUZZLE_BOARD_WIDTH = 800;
export const PUZZLE_BOARD_HEIGHT = 600;
export const PUZZLE_WORKSPACE_HEIGHT = 1000;
export const PUZZLE_SNAP_DISTANCE = 40;
export const PUZZLE_PLACED_Z_INDEX = 1;

export const puzzleProgressKey = (sessionKey) =>
  `quebraCabeca_progresso_${sessionKey}`;

export function clearPuzzleProgress(sessionKey) {
  if (!sessionKey || typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(puzzleProgressKey(sessionKey));
  } catch {}
}

export function readPuzzleProgress(sessionKey) {
  if (!sessionKey || typeof localStorage === "undefined") return null;
  try {
    const value = JSON.parse(
      localStorage.getItem(puzzleProgressKey(sessionKey)),
    );
    return Array.isArray(value) ? value : null;
  } catch {
    return null;
  }
}

export function savePuzzleProgress(sessionKey, pieces) {
  if (!sessionKey || typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(
      puzzleProgressKey(sessionKey),
      JSON.stringify(
        pieces.map(({ id, x, y, isPlaced }) => ({ id, x, y, isPlaced })),
      ),
    );
  } catch (error) {
    console.warn(
      "Não foi possível salvar o progresso do quebra-cabeça:",
      error,
    );
  }
}

export function createPuzzleConnections(rows, columns, random = Math.random) {
  const vertical = Array.from({ length: Math.max(0, rows - 1) }, () =>
    Array.from({ length: columns }, () => (random() > 0.5 ? 1 : -1)),
  );
  const horizontal = Array.from({ length: rows }, () =>
    Array.from({ length: Math.max(0, columns - 1) }, () =>
      random() > 0.5 ? 1 : -1,
    ),
  );
  return { vertical, horizontal };
}

export function createPuzzlePiecePath({
  top,
  right,
  bottom,
  left,
  pieceWidth,
  pieceHeight,
  paddingX,
  paddingY,
}) {
  const tabHalfWidth = pieceWidth * 0.15;
  const horizontalTabDepth = pieceWidth * 0.2;
  const verticalTabDepth = pieceHeight * 0.2;
  const centerX = paddingX + pieceWidth / 2;
  const centerY = paddingY + pieceHeight / 2;
  const rightX = paddingX + pieceWidth;
  const bottomY = paddingY + pieceHeight;
  let path = `M ${paddingX} ${paddingY}`;

  if (top === 0) path += ` L ${rightX} ${paddingY}`;
  else {
    path += ` L ${centerX - tabHalfWidth} ${paddingY}`;
    path += ` C ${centerX - tabHalfWidth} ${paddingY - top * verticalTabDepth * 0.5}, ${centerX - tabHalfWidth * 1.5} ${paddingY - top * verticalTabDepth}, ${centerX} ${paddingY - top * verticalTabDepth}`;
    path += ` C ${centerX + tabHalfWidth * 1.5} ${paddingY - top * verticalTabDepth}, ${centerX + tabHalfWidth} ${paddingY - top * verticalTabDepth * 0.5}, ${centerX + tabHalfWidth} ${paddingY}`;
    path += ` L ${rightX} ${paddingY}`;
  }

  if (right === 0) path += ` L ${rightX} ${bottomY}`;
  else {
    path += ` L ${rightX} ${centerY - tabHalfWidth}`;
    path += ` C ${rightX + right * horizontalTabDepth * 0.5} ${centerY - tabHalfWidth}, ${rightX + right * horizontalTabDepth} ${centerY - tabHalfWidth * 1.5}, ${rightX + right * horizontalTabDepth} ${centerY}`;
    path += ` C ${rightX + right * horizontalTabDepth} ${centerY + tabHalfWidth * 1.5}, ${rightX + right * horizontalTabDepth * 0.5} ${centerY + tabHalfWidth}, ${rightX} ${centerY + tabHalfWidth}`;
    path += ` L ${rightX} ${bottomY}`;
  }

  if (bottom === 0) path += ` L ${paddingX} ${bottomY}`;
  else {
    path += ` L ${centerX + tabHalfWidth} ${bottomY}`;
    path += ` C ${centerX + tabHalfWidth} ${bottomY + bottom * verticalTabDepth * 0.5}, ${centerX + tabHalfWidth * 1.5} ${bottomY + bottom * verticalTabDepth}, ${centerX} ${bottomY + bottom * verticalTabDepth}`;
    path += ` C ${centerX - tabHalfWidth * 1.5} ${bottomY + bottom * verticalTabDepth}, ${centerX - tabHalfWidth} ${bottomY + bottom * verticalTabDepth * 0.5}, ${centerX - tabHalfWidth} ${bottomY}`;
    path += ` L ${paddingX} ${bottomY}`;
  }

  if (left === 0) path += ` L ${paddingX} ${paddingY}`;
  else {
    path += ` L ${paddingX} ${centerY + tabHalfWidth}`;
    path += ` C ${paddingX - left * horizontalTabDepth * 0.5} ${centerY + tabHalfWidth}, ${paddingX - left * horizontalTabDepth} ${centerY + tabHalfWidth * 1.5}, ${paddingX - left * horizontalTabDepth} ${centerY}`;
    path += ` C ${paddingX - left * horizontalTabDepth} ${centerY - tabHalfWidth * 1.5}, ${paddingX - left * horizontalTabDepth * 0.5} ${centerY - tabHalfWidth}, ${paddingX} ${centerY - tabHalfWidth}`;
    path += ` L ${paddingX} ${paddingY}`;
  }
  return path;
}

export function createPuzzlePieces({ rows, columns, random = Math.random }) {
  const pieceWidth = PUZZLE_BOARD_WIDTH / columns;
  const pieceHeight = PUZZLE_BOARD_HEIGHT / rows;
  const paddingX = pieceWidth * 0.3;
  const paddingY = pieceHeight * 0.3;
  const trayHeight = PUZZLE_WORKSPACE_HEIGHT - PUZZLE_BOARD_HEIGHT;
  const connections = createPuzzleConnections(rows, columns, random);
  const pieces = [];

  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      const top = row === 0 ? 0 : -connections.vertical[row - 1][column];
      const right =
        column === columns - 1 ? 0 : connections.horizontal[row][column];
      const bottom = row === rows - 1 ? 0 : connections.vertical[row][column];
      const left = column === 0 ? 0 : -connections.horizontal[row][column - 1];
      pieces.push({
        id: `p_${row}_${column}`,
        row,
        column,
        top,
        right,
        bottom,
        left,
        path: createPuzzlePiecePath({
          top,
          right,
          bottom,
          left,
          pieceWidth,
          pieceHeight,
          paddingX,
          paddingY,
        }),
        x: random() * (PUZZLE_BOARD_WIDTH - (pieceWidth + paddingX * 2)),
        y:
          PUZZLE_BOARD_HEIGHT +
          20 +
          random() * (trayHeight - (pieceHeight + paddingY * 2) - 40),
        correctX: column * pieceWidth - paddingX,
        correctY: row * pieceHeight - paddingY,
        isPlaced: false,
        zIndex: 10,
      });
    }
  }
  return pieces;
}

export function restorePuzzlePieces(pieces, progress) {
  if (!Array.isArray(progress) || progress.length !== pieces.length) {
    return pieces;
  }
  const byId = Object.fromEntries(progress.map((piece) => [piece.id, piece]));
  return pieces.map((piece) => {
    const saved = byId[piece.id];
    if (!saved) return piece;
    return {
      ...piece,
      x: Number.isFinite(saved.x) ? saved.x : piece.x,
      y: Number.isFinite(saved.y) ? saved.y : piece.y,
      isPlaced: Boolean(saved.isPlaced),
      zIndex: saved.isPlaced ? PUZZLE_PLACED_Z_INDEX : piece.zIndex,
    };
  });
}

export const isPuzzlePieceNearTarget = (
  piece,
  x,
  y,
  distance = PUZZLE_SNAP_DISTANCE,
) => Math.hypot(x - piece.correctX, y - piece.correctY) < distance;

export const clampPuzzlePosition = (value, minimum, maximum) =>
  Math.max(minimum, Math.min(value, maximum));
